<template>
  <ReviewsProductInsightsCompetition
    :product="product"
    :benchmark="view.benchmark"
    :visibility="view.visibility"
    :competitors="view.competitors"
    :competitors-caption="view.competitorsCaption"
    :attributes="view.attributes"
    :pricing="view.pricing"
    :states="view.states"
    :loading="loading"
    :products-loading="productsLoading"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { type ProductById, useCompetitionInsightsQuery, useInsightsProductsBySlugsQuery } from '@/gql/reviews'
import { competitionCompetitorSlugs, mapCompetitionInsights } from '@/gql/reviews/mappers/insights'

import { timeframeToWindowKey } from '../insightsTimeframe'
import { comparisonWindow, isoToday } from '../insightsWindow'
import { useRouterQueryInsightsCategory } from '../useRouterQueryInsightsCategory'
import { useRouterQueryInsightsTimeframe } from '../useRouterQueryInsightsTimeframe'
import ReviewsProductInsightsCompetition from './ReviewsProductInsightsCompetition.vue'

const props = defineProps<{ product: ProductById }>()

const { locale } = useI18n()
const { category } = useRouterQueryInsightsCategory(() => props.product)
const { timeframe, periodPreset } = useRouterQueryInsightsTimeframe()

const shareOfVoice = computed(() => comparisonWindow(timeframe.value, new Date()))

const { result, loading } = useCompetitionInsightsQuery(
  () => ({
    productSlug: props.product.slug,
    categorySlug: category.value,
    period: periodPreset.value,
    window: timeframeToWindowKey(timeframe.value),
    shareOfVoiceStart: shareOfVoice.value.previousStart ?? shareOfVoice.value.start,
    shareOfVoiceEnd: isoToday(new Date()),
    covisitationStart: shareOfVoice.value.start,
    covisitationEnd: isoToday(new Date()),
  }),
  { clientId: 'reviews', keepPreviousResult: true, fetchPolicy: 'cache-first' },
)

const slugs = computed(() => competitionCompetitorSlugs(result.value, shareOfVoice.value, props.product.slug))

const { result: productsResult, loading: productsLoading } = useInsightsProductsBySlugsQuery(
  () => ({ slugs: slugs.value }),
  () => ({ clientId: 'reviews', fetchPolicy: 'cache-first', enabled: slugs.value.length > 0 }),
)

const view = computed(() =>
  mapCompetitionInsights(result.value, {
    productSlug: props.product.slug,
    products: productsResult.value?.listProducts ?? [],
    locale: locale.value,
    comparisonWindow: shareOfVoice.value,
  }),
)
</script>
